import { CanvasEngineExportEntityType } from './../../lib/CanvasEngine/types/index'
import { defineStore, getActivePinia } from 'pinia'
import { CANVAS_HANDLE_MODE, CanvasEngine, CanvasEngineEntity, SHAPES } from '@/lib/CanvasEngine'
import { ref, createApp, watch } from 'vue'
import { SnapManager } from '@/lib/CanvasEngine/entity/base/balloon/snapManager'
import { useAppEditorFacilityStore, useEditorStore } from './editor'
import {
  ShapeForDocs,
  TripodShapePoints,
  UserSettingConfig,
  UserSettingConfigConv,
} from '../data/type'
import { CanvasEngineRender } from '@/lib/CanvasEngine/renderer'
import { App } from '..'
import { getCurrentColor, getCurrentColorName, shapeColorItems } from '../constant'
import { isWeb } from '@/lib/Capacitor'
import { useWebEditorDataStore, useWebEditorFacilityStore } from './webeditor'
import { useDefectPhotoStore } from '../data/Editor/defectPhoto'
import { convertUserSettingToCanvasStore } from '../util'
import ActionButtonGroup from '@/components/Editor/ActionButtonGroup.vue'
import { useShapeStore } from '../data/Editor/shape'
import { cloneDeep } from 'lodash'
import { useAllTaskStore } from './task'
import { canvasShapeData, findUserSettingValue, getRelatedShapes } from '../function'
import { getDefectDisplayMode, resolveSafeDisplayColors } from '../function/defectMode'
import { isApp } from '../..'
import { useAppStateStore } from './state'
import { CanvasNotFoundHandler, Defect } from '../data/types'
import UTIL from '../../lib/CanvasEngine/helper/canvasUtil'

export const useCanvasStore = defineStore('canvas', () => {
  let canvasEngine: CanvasEngine | undefined
  let canvasRenderer: CanvasEngineRender | undefined

  // 복사버튼 관련 상태 (any 타입으로 타입 충돌 해결)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ButtonApps = ref<Map<string, any>>(new Map())
  const ButtonContainers = ref<Map<string, HTMLElement>>(new Map())

  const balloonScale = ref<number>(1.0)
  const connectedLineThicknessScale = ref<number>(1.0)
  const balloonFontScale = ref<number>(1.0)
  const pointShapeScale = ref<number>(1.0)
  const areaBorderThicknessScale = ref<number>(1.0)
  const balloonFontColor = ref<string>('#000000')
  const balloonFillColor = ref<string>('#000000')
  const balloonBorderColor = ref<string>('#000000')
  const balloonFillOpacity = ref<number>(1)

  /**
   * canvasRenderer 지연 초기화
   */
  function getCanvasRenderer(): CanvasEngineRender {
    if (!canvasRenderer) {
      canvasRenderer = CanvasEngineRender.prepare({
        container: document.createElement('div'),
        width: 0,
        height: 0,
      })
    }
    return canvasRenderer
  }

  const isSnappingEnabled = ref<boolean>(true)

  // sortEntities 등으로 인덱스가 변경될 때 증가하는 반응형 버전 카운터
  const indexVersion = ref<number>(0)
  function bumpIndexVersion(): void {
    indexVersion.value++
  }

  // isSnappingEnabled 값이 변경될 때 SnapManager의 static 상태도 동기화
  watch(
    isSnappingEnabled,
    newValue => {
      SnapManager.setSnappingEnabled(newValue)
    },
    { immediate: true },
  )

  const isAllFloorNumbering = ref<boolean>(false)

  const canvasSelectedShapeId = ref<string | undefined>(undefined)
  // 모드변경 감지를 위한 ref
  const canvasHandleMode = ref<CANVAS_HANDLE_MODE | null>(null)

  // grabBox 관련 상태
  const grabbedEntityIds = ref<string[]>([])
  const grabBoxRect = ref<{ x: number; y: number; width: number; height: number } | null>(null)
  const isGrabMode = ref<boolean>(false)
  const isMobileSelectMode = ref<boolean>(false)

  // 외부(ShapeInspectPopup 등)에서 DefectList의 pending link 모드 진입 요청
  const pendingLinkRequest = ref('')
  function requestPendingLink(defectId: string): void {
    pendingLinkRequest.value = defectId
  }
  function clearPendingLinkRequest(): void {
    pendingLinkRequest.value = ''
  }

  // 개인 설정에 따라 캔버스 설정값을 초기화
  function initUserSettings(): void {
    const userConfig = useAllTaskStore().userSettingConfig
    if (userConfig.length > 0) {
      // shapeScale
      // const scaleValue = findUserSettingValue(userConfig, 'style', 'balloonSize')
      // if (scaleValue !== undefined && typeof scaleValue === 'number') {
      //   canvasEngine?.setBalloonScale(scaleValue)
      // }
      // isAllFloorNumbering
      const isAllFloorNumberingValue = findUserSettingValue(userConfig, 'shape', 'shapeNumbering')
      if (
        isAllFloorNumberingValue !== undefined &&
        (isAllFloorNumberingValue === 0 || isAllFloorNumberingValue === 1)
      ) {
        isAllFloorNumbering.value = isAllFloorNumberingValue === 1
      }
    }
  }

  // 스토어 초기화 시 사용자 설정 적용
  initUserSettings()

  /**
   * 액션버튼그룹을 표시하는 함수
   * @param container - 버튼그룹을 렌더링할 HTML 컨테이너
   * @param shapeId - shape ID
   */
  function showButton(container: HTMLElement, shapeId: string): void {
    // 이미 존재하는 버튼그룹이 있다면 제거
    hideAllButtons()

    // 개별 도형 케이스 처리
    const entity = canvasEngine?.getEntityById(shapeId)
    if (!entity) return

    // Vue 앱 생성
    const app = createApp(ActionButtonGroup, {
      isBrush: entity.isBrush(),
      onCopy: (copiedShapeId: string) => handleCopyShape(copiedShapeId),
      onAttach: (attachShapeId: string) => handleAttachShape(attachShapeId),
      onDelete: (deletedShapeIds: string[]) => handleDeleteShape(deletedShapeIds),
      onCombine: (combineShapeIds: string[]) => handleCombineShape(combineShapeIds),
      onUngroup: (ungroupShapeId: string) => handleUngroupShape(ungroupShapeId),
      onTripodTail: (tripodTailShapeId: string) => handleTripodTailShape(tripodTailShapeId),
    })

    // Pinia 명시적 설치 (activePinia 글로벌 의존 제거)
    const pinia = getActivePinia()
    if (pinia) {
      app.use(pinia)
    }

    // 앱 마운트
    app.mount(container)

    // entity에 컨테이너 엘리먼트 설정
    const trySetElement = (): boolean => {
      const el = container.firstElementChild as HTMLElement
      if (el) {
        entity.setButtonElement(el)
        return true
      }
      return false
    }

    if (!trySetElement()) {
      // v-if 렌더링 지연 대응: MutationObserver로 DOM 변화 감시
      let settled = false
      const observer = new MutationObserver(() => {
        if (!settled && trySetElement()) {
          settled = true
          observer.disconnect()
        }
      })
      observer.observe(container, { childList: true, subtree: true })

      // 안전장치: 500ms 후 observer 정리 + 최종 재시도
      setTimeout(() => {
        if (!settled) {
          settled = true
          observer.disconnect()
          trySetElement()
        }
      }, 500)
    }

    // 상태 저장
    ButtonApps.value.set(shapeId, app)
    ButtonContainers.value.set(shapeId, container)
  }

  /**
   * 복사버튼을 숨기는 함수
   * @param shapeId - shape ID
   */
  function hideButton(shapeId: string): void {
    const app = ButtonApps.value.get(shapeId)
    const container = ButtonContainers.value.get(shapeId)

    if (app) {
      app.unmount()
      ButtonApps.value.delete(shapeId)
    }

    if (container) {
      ButtonContainers.value.delete(shapeId)
    }
  }

  /**
   * 모든 복사버튼을 숨기는 함수
   */
  function hideAllButtons(): void {
    ButtonApps.value.forEach((_, shapeId) => {
      hideButton(shapeId)
    })
  }

  /**
   * Defect 복사 핸들러
   * @param shapeId - 복사할 shape ID
   */
  async function handleCopyShape(shapeId: string): Promise<void> {
    try {
      if (!canvasEngine) return
      const shapeStore = useShapeStore()
      const dataStore = useWebEditorDataStore()

      // 현재 shape 정보 가져오기
      const currentShape = App.isApp() ? shapeStore.GetItem(shapeId) : dataStore.getShape(shapeId)

      if (!currentShape) {
        App.toastInstance.add('error', '복사할 도형을 찾을 수 없습니다.')
        return
      }

      // 자식 shape인 경우 부모 shape를 찾아서 복사
      let targetShapeId = shapeId
      if (currentShape.parent_defect_id) {
        if (App.isApp()) {
          const relatedShapes = shapeStore.getCurrentRelatedShapes(shapeId)
          const parentShape = relatedShapes.find(shape => shape.parent_defect_id)
          if (parentShape) targetShapeId = parentShape.localStringId
        } else {
          const allShapes = dataStore.getShapes(currentShape.floorplan_id || '')
          const relatedShapes = getRelatedShapes(shapeId, allShapes)
          const parentShape = relatedShapes.find(shape => shape.parent_defect_id)
          if (parentShape) targetShapeId = parentShape.id.toString()
        }
      }

      const entity = canvasEngine.getEntityById(targetShapeId)
      if (!entity) {
        App.toastInstance.add('error', '복사할 도형을 찾을 수 없습니다.')
        return
      }
      const entityColor = entity.getColor()
      if (getCurrentColorName(entityColor) == 'purple') {
        App.toastInstance.add('error', '전차항목은 복사할 수 없습니다.')
        return
      } else {
        if (isApp()) {
          if (shapeStore.isCheckPreviousShape(targetShapeId)) {
            App.toastInstance.add('error', '전차항목은 복사할 수 없습니다.')
            return
          }
        } else {
          if (useWebEditorDataStore().isCheckPreviousShape(targetShapeId)) {
            App.toastInstance.add('error', '전차항목은 복사할 수 없습니다.')
            return
          }
        }
      }

      // 도형 데이터 내보내기
      const exportData = cloneDeep(canvasEngine.exportEntity(targetShapeId))
      // 새로운 위치 계산 (기존 위치에서 조금 오프셋)
      const offset = 50

      let point = exportData.point
      let points = exportData.points
      let balloonPoint = exportData.balloonPoint

      if (exportData.type === SHAPES.DIRECTION) {
        // 방향 도형인 경우 모든 점과 벌룬의 x좌표를 직접 이동
        points = exportData.points.map((p, i) => (i % 2 === 0 ? p + offset : p))
        balloonPoint = {
          x: exportData.balloonPoint.x + offset,
          y: exportData.balloonPoint.y,
        }
      } else {
        // 아니면 기준점(point)의 x좌표만 이동
        point = {
          x: exportData.point.x + offset,
          y: exportData.point.y,
        }
      }

      const newExportData: CanvasEngineExportEntityType = {
        ...exportData,
        point,
        points,
        balloonPoint,
        shapeId: canvasEngine.generateShapeId(), // 새로운 ID 생성
        color: entityColor,
      }

      // 새로운 도형 생성 (addShape와 동일한 패턴)
      const newEntity = canvasEngine.importEntity(newExportData)
      newEntity.changeColor(entityColor)
      if (newExportData.rectType) {
        newEntity.setShapeStyleToggle(newExportData.rectType)
      }
      if (newExportData.dotType) {
        newEntity.setIndividualDotTypeSetting(newExportData.dotType)
        newEntity.setDotStyleToggle(newExportData.dotType)
      }
      if (newExportData.isFill) {
        newEntity.setShapeFillToggle(newExportData.isFill)
      }
      if (newExportData.balloonFontColor) {
        newEntity.setBalloonFontColor(newExportData.balloonFontColor)
        newEntity.changeBalloonFontColor(newExportData.balloonFontColor)
      }
      if (newExportData.balloonFillColor) {
        newEntity.setBalloonFillColor(newExportData.balloonFillColor)
        newEntity.changeBalloonFillColor(newExportData.balloonFillColor)
      }
      newEntity.enableIndex()
      canvasEngine.updateIndex()

      // 데이터 복사 (addShape와 달리 별도 저장 필요)
      if (App.isApp()) {
        shapeStore.copyShapeToHistory(targetShapeId, newExportData)
      } else {
        // 웹 환경에서는 서버 ID가 반환되지만, Canvas Engine에서는 자체 ID 사용
        const newShape = await dataStore.copyShapeToHistory(targetShapeId, newExportData)
        if (!newShape) return
        newEntity.setId(newShape.id.toString())
        canvasEngine?.selectEntityById(newShape.id.toString())
      }

      App.toastInstance.add('success', '결함이 복제되었습니다.')
    } catch (error) {
      console.error('도형 복사 중 오류:', error)
      App.toastInstance.add('error', '도형 복사 중 오류가 발생했습니다.')
    }
  }

  // 도형에 결함 추가 핸들러
  async function handleAttachShape(shapeId: string): Promise<void> {
    // 최대 자식 결함 개수 (본 결함 포함 총 5개 = 자식 4개)
    const MAX_CHILD_SHAPES = 4

    try {
      if (!canvasEngine) return
      const shapeStore = useShapeStore()
      const dataStore = useWebEditorDataStore()

      // 현재 shape 정보 가져오기
      const currentShape = App.isApp() ? shapeStore.GetItem(shapeId) : dataStore.getShape(shapeId)

      if (!currentShape) {
        App.toastInstance.add('error', '추가할 도형을 찾을 수 없습니다.')
        return
      }

      // 자식 shape인 경우 부모 shape를 찾아서 attach
      let targetShapeId = shapeId
      let parentShapeId: string | number = shapeId
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let relatedShapes: any[] = []

      if (currentShape.parent_defect_id) {
        if (App.isApp()) {
          relatedShapes = shapeStore.getCurrentRelatedShapes(shapeId)
          const parentShape = relatedShapes.find(shape => !shape.parent_defect_id)
          if (parentShape) {
            targetShapeId = parentShape.localStringId
            parentShapeId = parentShape.localStringId
          }
        } else {
          const allShapes = dataStore.getShapes(currentShape.floorplan_id || '')
          relatedShapes = getRelatedShapes(shapeId, allShapes)
          const parentShape = relatedShapes.find(shape => !shape.parent_defect_id)
          if (parentShape) {
            targetShapeId = parentShape.id.toString()
            parentShapeId = parentShape.id
          }
        }
      } else {
        // 현재가 부모 shape인 경우 관련 shapes 가져오기
        if (App.isApp()) {
          relatedShapes = shapeStore.getCurrentRelatedShapes(shapeId)
        } else {
          const allShapes = dataStore.getShapes(currentShape.floorplan_id || '')
          relatedShapes = getRelatedShapes(shapeId, allShapes)
        }
      }

      // 현재 자식 결함 개수 확인 (부모가 아닌 shape들)
      const currentChildCount = relatedShapes.filter(shape => shape.parent_defect_id).length

      if (currentChildCount >= MAX_CHILD_SHAPES) {
        App.toastInstance.add('error', `최대 ${MAX_CHILD_SHAPES}개 까지만 추가할 수 있습니다.`)
        return
      }

      const entity = canvasEngine.getEntityById(targetShapeId)
      if (!entity) {
        App.toastInstance.add('error', '추가할 도형을 찾을 수 없습니다.')
        return
      }

      // 전차항목인지 확인
      const entityColor = entity.getColor()
      const isPurple = getCurrentColorName(entityColor) === 'purple'
      const isPrevious = isApp()
        ? shapeStore.isCheckPreviousShape(targetShapeId)
        : dataStore.isCheckPreviousShape(targetShapeId)

      if (isPurple || isPrevious) {
        App.toastInstance.add('error', '전차항목에는 추가할 수 없습니다.')
        return
      }

      // 도형 데이터 내보내기
      const exportData = cloneDeep(canvasEngine.exportEntity(targetShapeId))

      const newExportData: CanvasEngineExportEntityType = {
        ...exportData,
        shapeId: canvasEngine.generateShapeId(), // 새로운 ID 생성
        color: entityColor,
      }

      // 데이터 복사 (addShape와 달리 별도 저장 필요)
      if (App.isApp()) {
        // attach의 경우 Add 함수를 사용하여 자식으로 생성
        // 앱에서는 parent_shape_id를 localStringId로 저장
        await shapeStore.addShapeToHistory(
          newExportData,
          currentShape.floorplan_id.toString(),
          targetShapeId,
        )

        // 자식 결함은 캔버스에 추가하지 않고 데이터베이스에만 저장
        // 부모 shape는 캔버스에서 선택된 상태로 유지하고, 새로 생성된 자식을 책갈피에서 선택

        // 자식이 추가되었으므로 캔버스 인덱스 업데이트
        if (canvasEngine) canvasEngine.updateIndex()

        setTimeout(() => {
          const editorStore = useEditorStore()
          editorStore.setShapeView(true)
          // 자식 shape는 캔버스에 없으므로 setShapeId만 사용하여 책갈피 탭에서 선택
          setShapeId(newExportData.shapeId)

          // 한번더 캔버스 인덱스 업데이트
          if (canvasEngine) canvasEngine.updateIndex()
        }, 100)
      } else {
        const newShape = await dataStore.addShapeToHistory(
          newExportData,
          'custom',
          Number(parentShapeId),
          null,
          currentShape.floorplan_id.toString(),
        )
        if (!newShape) return
        // 웹 환경에서는 새로 생성된 자식 shape를 직접 선택
        dataStore.selectShape(newShape.id.toString())
      }

      App.toastInstance.add('success', '결함이 추가되었습니다.')
    } catch (error) {
      console.error('결함 추가 중 오류:', error)
      App.toastInstance.add('error', '결함 추가 중 오류가 발생했습니다.')
    }
  }

  // 결함 삭제 핸들러
  async function handleDeleteShape(shapeIds: string[]): Promise<void> {
    if (!canvasEngine) return
    const editorStore = useEditorStore()
    const dataStore = useWebEditorDataStore()
    const webEditorStore = useWebEditorDataStore()
    const shapeStore = useShapeStore()

    // 브러쉬 체크 (단일 shape인 경우에만)
    let isBrush = false
    if (shapeIds.length === 1) {
      const entity = canvasEngine.getEntityById(shapeIds[0])
      if (entity?.isBrush()) isBrush = true
    }

    // 삭제할 shape들과 관련 정보를 구하는 함수
    const getDeleteShapesData = (
      shapeDatabaseId: string,
    ): {
      shapesToDelete: string[]
      deleteCount: number
      hasRelatedShapes: boolean
      currentShape: Defect | null
    } => {
      let currentShape: Defect | undefined
      if (App.isApp()) {
        // 앱에서는 GetItem으로 먼저 찾고, 없으면 originShapes에서 직접 찾기
        currentShape = shapeStore.GetItem(shapeDatabaseId)
        if (!currentShape) {
          const directShape = shapeStore.originShapes.find(
            shape =>
              shape.localStringId == shapeDatabaseId || shape.id.toString() == shapeDatabaseId,
          )
          currentShape = directShape
        }
      } else {
        currentShape = dataStore.getShape(shapeDatabaseId)
      }

      if (!currentShape) {
        console.warn(`삭제할 shape를 찾을 수 없습니다: ${shapeDatabaseId}`)
        return { shapesToDelete: [], deleteCount: 0, hasRelatedShapes: false, currentShape: null }
      }

      // 앱은 localStringId, 웹은 id.toString() 사용
      let shapesToDelete: string[] = []

      // 부모 shape인 경우 - 관련된 모든 shape 삭제
      if (!currentShape.parent_defect_id) {
        // 앱
        if (App.isApp() && 'localStringId' in currentShape) {
          const allShapes = shapeStore.currentFloorBranchShapes(
            currentShape.floorplan_id.toString() || '',
          )
          const relatedShapes = getRelatedShapes(shapeDatabaseId, allShapes) as Defect[]
          shapesToDelete = relatedShapes.map((shape: Defect) => shape.localStringId)
        } else {
          // 웹
          const allShapes = dataStore.getShapes(currentShape.floorplan_id || '')
          const relatedShapes = getRelatedShapes(shapeDatabaseId, allShapes)
          shapesToDelete = relatedShapes.map((shape: Defect) => shape.id.toString())
        }
      } else {
        // 자식 shape인 경우 본인만 삭제
        shapesToDelete = [shapeDatabaseId]
      }

      return {
        shapesToDelete,
        deleteCount: shapesToDelete.length,
        hasRelatedShapes: shapesToDelete.length > 1,
        currentShape,
      }
    }

    // 삭제함수
    const deleteShapeFunc = async (
      shapesToDelete: string[],
      deleteCount: number,
      currentShape: Defect | null,
    ): Promise<void> => {
      if (!canvasEngine) return

      try {
        // 첫 번째 삭제할 shape의 인덱스를 사용
        const currentIndex = Number(canvasEngine.getIndex(shapesToDelete[0])) || 0
        // 자식 shape 삭제 시 부모 shape ID 미리 저장
        let parentShapeId: string | null = null
        if (currentShape && currentShape.parent_defect_id) {
          // 자식 shape이므로 부모 ID 저장
          parentShapeId = currentShape.parent_defect_id.toString()
        }

        // 브러쉬 체크
        const hasBrush = shapesToDelete.some(id => {
          const entity = canvasEngine?.getEntityById(id)
          return entity?.isBrush()
        })

        // 삭제 전에 grab 모드 종료 (리스닝 복원을 위해)
        canvasEngine.exitGrabMode()

        // 캔버스에서 제거
        UTIL.deleteMultipleEntities(shapesToDelete)

        // 캐시한번 리로드
        refreshCache()

        // 데이터 삭제
        if (shapesToDelete.length > 1 && !hasBrush) {
          // 여러 shape를 한 번에 삭제 (브러쉬가 아닌 경우)
          const currentIndices = new Map<string, number>()
          shapesToDelete.forEach(id => {
            const index = Number(canvasEngine?.getIndex(id)) || 0
            currentIndices.set(id, index)
          })

          if (App.isApp()) {
            await shapeStore.deleteMultipleShapesToHistory(shapesToDelete, currentIndices)
          } else {
            await dataStore.deleteMultipleShapesToHistory(shapesToDelete, currentIndices)
          }
        } else {
          // 단일 shape 또는 브러쉬 삭제
          const deletePromises = shapesToDelete.map(id =>
            App.isApp()
              ? shapeStore.deleteShapeToHistory(id, currentIndex)
              : dataStore.deleteShapeToHistory(id, currentIndex),
          )
          await Promise.all(deletePromises)
        }

        // 삭제 완료 후 리스닝 복원
        canvasEngine.restoreEntitiyListening()

        // 그랩 모드에서 1개 도형만 남은 경우 자동 선택 처리
        canvasEngine.checkAndHandleSingleGrab?.()

        // 환경별 추가 처리 - 자식 삭제 시 부모 선택, 그 외에는 선택 해제
        if (parentShapeId) {
          // 자식 shape 삭제 시 부모 선택
          if (App.isApp()) {
            editorStore.setShapeView(true)
            //앱은 localStringId로 저장되어 있으므로 변환해줘야 함
            const parentShape = shapeStore.originShapes.find(
              shape => shape.localStringId == parentShapeId || shape.id.toString() == parentShapeId,
            )
            if (parentShape) {
              selectShapeById(parentShape.localStringId)
            } else {
              throw new Error('부모 shape를 찾을 수 없습니다.')
            }
          } else {
            webEditorStore.selectShape(parentShapeId)
          }
        } else {
          // 부모 shape 삭제 또는 단일 shape 삭제 시 선택 해제
          if (App.isApp()) editorStore.setShapeView(false)
          else webEditorStore.selectShape('')
        }

        App.toastInstance.add(
          'success',
          `${hasBrush ? '자유그리기가' : deleteCount > 1 ? `관련된 결함 ${deleteCount}개가` : '결함이'} 삭제되었습니다.`,
        )
      } catch (error) {
        console.error('도형 삭제 중 오류:', error)
        App.toastInstance.add('error', '도형 삭제 중 오류가 발생했습니다.')
      }
    }

    if (isBrush) {
      deleteShapeFunc(shapeIds, 1, null)
      return
    }

    // 여러 shape ID 처리
    if (shapeIds.length > 1) {
      // 모든 대상 shape들에 대해 삭제할 shape들을 수집
      const allShapesToDelete = new Set<string>()
      const allCurrentShapes: Defect[] = []
      let totalRelatedShapes = 0

      for (const shapeId of shapeIds) {
        const deleteInfo = getDeleteShapesData(shapeId)
        if (deleteInfo.deleteCount > 0) {
          deleteInfo.shapesToDelete.forEach(id => allShapesToDelete.add(id))
          if (deleteInfo.currentShape) {
            allCurrentShapes.push(deleteInfo.currentShape)
          }
          if (deleteInfo.hasRelatedShapes) {
            totalRelatedShapes += deleteInfo.deleteCount - 1
          }
        }
      }

      const finalShapesToDelete = Array.from(allShapesToDelete)
      const totalDeleteCount = finalShapesToDelete.length

      if (totalDeleteCount === 0) {
        App.toastInstance.add('error', '삭제할 도형 정보를 찾을 수 없습니다.')
        return
      }

      const message =
        totalRelatedShapes > 0
          ? `선택된 ${shapeIds.length}개 도형과 관련된 ${totalRelatedShapes}개의 추가 결함도 함께 삭제됩니다.\n총 ${totalDeleteCount}개의 결함을 삭제하시겠어요?`
          : `선택된 ${shapeIds.length}개의 결함을 삭제하시겠어요?`

      App.confirmInstance.show({
        message,
        confirmText: '삭제',
        cancelText: '취소',
        onConfirm: async () => {
          deleteShapeFunc(finalShapesToDelete, totalDeleteCount, allCurrentShapes[0] || null)
        },
      })
      return
    }

    // 단일 shape 처리
    const deleteInfo = getDeleteShapesData(shapeIds[0])

    if (deleteInfo.deleteCount === 0) {
      App.toastInstance.add('error', '삭제할 도형 정보를 찾을 수 없습니다.')
      return
    }

    const message = deleteInfo.hasRelatedShapes
      ? `${deleteInfo.deleteCount - 1}개의 추가 결함도 함께 삭제됩니다.\n정말 삭제하시겠어요?`
      : '결함을 삭제하시겠어요?'

    App.confirmInstance.show({
      message,
      confirmText: '삭제',
      cancelText: '취소',
      onConfirm: async () => {
        deleteShapeFunc(deleteInfo.shapesToDelete, deleteInfo.deleteCount, deleteInfo.currentShape)
      },
    })
  }

  function getCanvasPointSize(): number {
    return canvasEngine?.pointSize || 3
  }

  /**
   * 삼발이(Tripod) 도형 생성
   * 여러 포인트 도형을 하나의 삼발이 도형으로 결합
   */
  async function handleCombineShape(combineShapeIds: string[]): Promise<void> {
    if (!canvasEngine) return
    const stateStore = useAppStateStore()
    stateStore.setIsLoading(true)
    try {
      // TRIPOD 변환 전에 그랩 모드 종료 (entity listening 복원)
      canvasEngine.exitGrabMode()

      // 1. 선택된 도형들의 정보 수집
      const pointShapes = combineShapeIds
        .map(id => canvasEngine?.getEntityById(id))
        .filter((entity): entity is CanvasEngineEntity => entity?.isPoint() === true)

      if (pointShapes.length === 0) {
        console.warn('선택된 도형 중 점 도형이 없습니다.')
        return
      }

      // 2. 시작-끝 쌍 데이터 구조 생성
      const pointPairs: Array<{
        start: { x: number; y: number }
        end: { x: number; y: number }
        balloonText: string
        color: string
        id: string
      }> = []

      pointShapes.forEach((entity, _index) => {
        // 시작점 (점 도형 자체의 위치)
        const entityPosition = entity.getEntityPosition()
        const startPoint = { x: entityPosition.x, y: entityPosition.y }

        // 끝점 (벌룬이 있으면 시작점 + 벌룬 상대좌표, 없으면 시작점과 동일)
        const balloonPosition = entity.getBalloonPosition()
        const endPoint = balloonPosition
          ? {
              x: entityPosition.x + balloonPosition.x,
              y: entityPosition.y + balloonPosition.y,
            }
          : { x: entityPosition.x, y: entityPosition.y }

        // 벌룬 텍스트
        const balloonText = entity.getBalloonText() || '(텍스트 없음)'

        // 색상
        const color = entity.getColor()

        // 쌍 데이터 추가
        pointPairs.push({
          start: startPoint,
          end: endPoint,
          balloonText,
          color,
          id: entity.getId(),
        })
      })

      const sortedPointPairs = pointPairs.sort(
        (a, b) => Number(a.balloonText) - Number(b.balloonText),
      )
      // 3. 수직/수평 결정 기준 계산
      const startPoints = pointPairs.map(pair => pair.start)

      // 좌표를 [x, y] 배열 형태로 변환 (일관성을 위해)
      const startPointsAsArrays: number[][] = startPoints.map(point => [point.x, point.y])
      // 모든 시작점들의 x좌표 차이들 계산 (배열 형태 사용)
      const xDifferences: number[] = []
      for (let i = 0; i < startPointsAsArrays.length; i++) {
        for (let j = i + 1; j < startPointsAsArrays.length; j++) {
          xDifferences.push(Math.abs(startPointsAsArrays[i][0] - startPointsAsArrays[j][0]))
        }
      }

      // 모든 시작점들의 y좌표 차이들 계산 (배열 형태 사용)
      const yDifferences: number[] = []
      for (let i = 0; i < startPointsAsArrays.length; i++) {
        for (let j = i + 1; j < startPointsAsArrays.length; j++) {
          yDifferences.push(Math.abs(startPointsAsArrays[i][1] - startPointsAsArrays[j][1]))
        }
      }

      // 평균 계산
      const xDiffAverage = xDifferences.reduce((sum, diff) => sum + diff, 0) / xDifferences.length
      const yDiffAverage = yDifferences.reduce((sum, diff) => sum + diff, 0) / yDifferences.length

      const isVertical = xDiffAverage < yDiffAverage

      const tripodShapePoints: TripodShapePoints = {
        mainAxisPoint1: [0, 0],
        mainAxisPoint2: [0, 0],
        dots: startPointsAsArrays, // 일관된 배열 형태 사용
        mainShapeId: sortedPointPairs[0].id,
        targetIds: sortedPointPairs.map(pair => pair.id),
        tailStyle: 'dot', // 기본값으로 dot 설정
      }
      if (isVertical) {
        // 수직 주축선 - 조건부 위치 계산

        // 모든 시작점의 x가 모든 끝점의 x보다 낮은지 확인
        const maxStartX = Math.max(...sortedPointPairs.map(pair => pair.start.x))
        const minEndX = Math.min(...sortedPointPairs.map(pair => pair.end.x))
        const allStartsLeftOfEnds = maxStartX < minEndX

        let axisX: number
        if (allStartsLeftOfEnds) {
          // 시작점들이 모두 끝점들보다 왼쪽에 있으면: 시작점 최대값 + 60
          axisX = maxStartX + 60
        } else {
          // 끝점들 중 최소 x를 가진 point 찾기
          const lowestXEndPoint = sortedPointPairs.reduce((lowest, pair) =>
            pair.end.x < lowest.end.x ? pair : lowest,
          )

          // 해당 끝점과 시작점 사이의 거리 계산
          const distanceToStart = Math.abs(lowestXEndPoint.end.x - lowestXEndPoint.start.x)

          // 거리가 20 이내면 주축선을 왼쪽으로 이동 (시작점 기준으로 -60)
          if (distanceToStart <= 20) {
            axisX = lowestXEndPoint.start.x - 60
          } else {
            // 그렇지 않으면 기존 로직: 끝점들 중 최소 x
            axisX = lowestXEndPoint.end.x
          }
        }

        // y 범위 계산 (가장 낮은 y ~ 가장 높은 y) - 시작점 기준
        const minY = Math.min(...startPointsAsArrays.map(p => p[1]))
        const maxY = Math.max(...startPointsAsArrays.map(p => p[1]))

        tripodShapePoints.mainAxisPoint1 = [axisX, minY]
        tripodShapePoints.mainAxisPoint2 = [axisX, maxY]
      } else {
        // 수평 주축선 - 조건부 위치 계산

        // 모든 시작점의 y가 모든 끝점의 y보다 높은지 확인
        const minStartY = Math.min(...sortedPointPairs.map(pair => pair.start.y))
        const maxEndY = Math.max(...sortedPointPairs.map(pair => pair.end.y))
        const allStartsAboveEnds = minStartY > maxEndY

        let axisY: number
        if (allStartsAboveEnds) {
          // 시작점들이 모두 끝점들보다 위에 있으면: 시작점 최소값 - 60
          axisY = minStartY - 60
        } else {
          // 그렇지 않으면: 끝점들 중 최소 y
          const lowestYEndPoint = sortedPointPairs.reduce((lowest, pair) =>
            pair.end.y < lowest.end.y ? pair : lowest,
          )
          axisY = lowestYEndPoint.end.y
        }

        // x 범위 계산 (가장 낮은 x ~ 가장 높은 x) - 시작점 기준
        const minX = Math.min(...startPointsAsArrays.map(p => p[0]))
        const maxX = Math.max(...startPointsAsArrays.map(p => p[0]))

        tripodShapePoints.mainAxisPoint1 = [minX, axisY]
        tripodShapePoints.mainAxisPoint2 = [maxX, axisY]
      }

      // 4. TRIPOD balloon 위치 계산 (시작점들의 반대편)
      const mainAxisStart = tripodShapePoints.mainAxisPoint1
      const mainAxisEnd = tripodShapePoints.mainAxisPoint2
      const isVerticalMainAxis =
        Math.abs(mainAxisEnd[1] - mainAxisStart[1]) > Math.abs(mainAxisEnd[0] - mainAxisStart[0])

      let balloonPoint: { x: number; y: number }

      if (isVerticalMainAxis) {
        // 수직 주축선: 시작점들의 평균 x와 주축선 x 비교
        const avgStartX =
          tripodShapePoints.dots.reduce((sum, dot) => sum + dot[0], 0) /
          tripodShapePoints.dots.length
        const axisX = mainAxisStart[0]
        const balloonOnRightSide = avgStartX < axisX // 시작점들이 주축선 왼쪽에 있으면 balloon은 오른쪽

        // 주축선의 중간 지점에서 y 계산
        const axisMiddleY = (mainAxisStart[1] + mainAxisEnd[1]) / 2

        // balloon 위치: 주축선에서 좌우로 100px 떨어진 곳
        balloonPoint = {
          x: axisX + (balloonOnRightSide ? 100 : -100),
          y: axisMiddleY,
        }
      } else {
        // 수평 주축선: 시작점들의 평균 y와 주축선 y 비교
        const avgStartY =
          tripodShapePoints.dots.reduce((sum, dot) => sum + dot[1], 0) /
          tripodShapePoints.dots.length
        const axisY = mainAxisStart[1]
        const balloonBelowAxis = avgStartY > axisY // 시작점들이 주축선 아래에 있으면 balloon은 위쪽

        // 주축선의 중간 지점에서 x 계산
        const axisMiddleX = (mainAxisStart[0] + mainAxisEnd[0]) / 2

        // balloon 위치: 주축선에서 상하로 100px 떨어진 곳
        balloonPoint = {
          x: axisMiddleX,
          y: axisY + (balloonBelowAxis ? -100 : 100),
        }
      }

      // 5. TRIPOD shape 생성
      if (!canvasEngine) return

      // 첫 번째 point의 색상을 TRIPOD의 색상으로 사용
      const firstPointEntity = canvasEngine.getEntityById(sortedPointPairs[0].id)
      const tripodColor = sortedPointPairs[0].color

      // 첫 번째 point의 balloon 설정들 추출
      let balloonFillColor: string | undefined
      let balloonFontColor: string | undefined
      let balloonScale: number | undefined
      let connectedLineThicknessScale: number | undefined
      let balloonFontScale: number | undefined
      let pointShapeScale: number | undefined
      let areaBorderThicknessScale: number | undefined

      if (firstPointEntity) {
        balloonFillColor = firstPointEntity.getIndividualBalloonFillColor()
        balloonFontColor = firstPointEntity.getIndividualBalloonFontColor()
        balloonScale = firstPointEntity.getIndividualBalloonScale()
        connectedLineThicknessScale = firstPointEntity.getIndividualConnectedLineThicknessScale()
        balloonFontScale = firstPointEntity.getIndividualBalloonFontScale()
        pointShapeScale = firstPointEntity.getIndividualPointShapeScale()
        areaBorderThicknessScale = firstPointEntity.getIndividualAreaBorderThicknessScale()
      }

      // TRIPOD 생성을 위한 중심점 계산 (모든 dots의 중심)
      const centerX =
        tripodShapePoints.dots.reduce((sum, dot) => sum + dot[0], 0) / tripodShapePoints.dots.length
      const centerY =
        tripodShapePoints.dots.reduce((sum, dot) => sum + dot[1], 0) / tripodShapePoints.dots.length

      // 새로운 TRIPOD entity 생성 (첫 번째 point의 balloon 설정들 전달)
      const tripodShapeId = canvasEngine.generateShapeId()
      const tripodEntity = new CanvasEngineEntity(canvasEngine, {
        id: tripodShapeId,
        type: SHAPES.TRIPOD,
        createPoint: { x: centerX, y: centerY },
        balloonPoint: balloonPoint, // balloon을 주축선 반대편에 배치
        createConfig: {
          color: tripodColor,
          tripodShapePoints: tripodShapePoints,
        },
        layerId: 'default-layer',
        balloonFillColor: balloonFillColor,
        balloonFontColor: balloonFontColor,
        balloonScale: balloonScale,
        connectedLineThicknessScale: connectedLineThicknessScale,
        balloonFontScale: balloonFontScale,
        pointShapeScale: pointShapeScale,
        areaBorderThicknessScale: areaBorderThicknessScale,
        interactionMode: true, // 명시적으로 interaction 활성화
      })

      tripodEntity.changeColor(tripodColor)
      tripodEntity.enableIndex()
      tripodEntity.setPointerListening(true) // 명시적으로 listening 활성화

      // 5. 원본 point shape들 삭제
      sortedPointPairs.forEach(pair => {
        canvasEngine?.removeEntity(pair.id)
      })
      let currentId = tripodShapeId
      // 6. TRIPOD 데이터 저장 (웹 환경에서만)
      if (!App.isApp()) {
        // 웹 환경: 서버에 TRIPOD 저장 및 원본 point들 삭제
        try {
          const tripodExportData = canvasEngine.exportEntity(tripodShapeId)
          if (!tripodExportData) {
            throw new Error('TRIPOD export 데이터를 생성할 수 없습니다.')
          }

          // 데이터스토어에서 TRIPOD 생성 및 원본 point들 삭제
          const dataStore = useWebEditorDataStore()
          const targetShapeIds = sortedPointPairs.map(p => p.id)
          const savedTripodShape = await dataStore.addTripodShapeToHistory(
            tripodExportData,
            targetShapeIds,
          )
          tripodEntity.setId(savedTripodShape.id.toString())
          // canvasEngine?.selectEntityById(savedTripodShape.id.toString())
          currentId = savedTripodShape.id.toString()
          tripodEntity?.changeColor(tripodColor)
          tripodEntity?.setBalloonFontColor(balloonFontColor!)
          tripodEntity?.setBalloonFillColor(balloonFillColor!)
          tripodEntity?.enableIndex()

          const facilityStore = useWebEditorFacilityStore()
          const floorShapes = dataStore.getSortedShapes(
            parseInt(facilityStore.selectedFloorDatabaseId),
          )
          sortEntities(floorShapes.map(shape => shape.id.toString()))
        } catch (_error) {
          App.toastInstance.add('error', 'TRIPOD 저장 중 오류가 발생했습니다.')

          // 오류 시 캔버스에서 TRIPOD 제거 및 원본 point들 복원
          canvasEngine.removeEntity(tripodShapeId)
          // Note: 실제 구현에서는 원본 point들을 복원하는 로직이 필요할 수 있음
        }
      } else {
        // 앱 환경: shape store를 통한 TRIPOD 저장
        try {
          const tripodExportData = canvasEngine.exportEntity(tripodShapeId)
          if (!tripodExportData) {
            throw new Error('TRIPOD export 데이터를 생성할 수 없습니다.')
          }

          // 앱 shape store에서 TRIPOD 생성 및 원본 point들 삭제
          const { useShapeStore } = await import('@/app/data/Editor/shape')
          const shapeStore = useShapeStore()
          const targetShapeIds = sortedPointPairs.map(p => p.id)
          const savedTripodShape = await shapeStore.addTripodShapeToHistory(
            tripodShapeId,
            tripodExportData,
            targetShapeIds,
          )

          // Canvas entity ID 업데이트
          tripodEntity.setId(savedTripodShape.id.toString())
          canvasEngine?.selectEntityById(savedTripodShape.id.toString())

          tripodEntity?.changeColor(tripodColor)
          tripodEntity?.enableIndex()
          tripodEntity?.setBalloonFontColor(balloonFontColor!)
          tripodEntity?.setBalloonFillColor(balloonFillColor!)
          canvasEngine.selectEntityById(savedTripodShape.id.toString())

          const facilityStore = useAppEditorFacilityStore()
          const floorShapes = shapeStore.getSortedShapes(facilityStore.selectedFloorDatabaseId)
          sortEntities(floorShapes.map(shape => shape.id.toString()))
        } catch (_error) {
          console.error('앱 환경 TRIPOD 저장 오류:', _error)
          App.toastInstance.add('error', 'TRIPOD 저장 중 오류가 발생했습니다.')

          // 오류 시 캔버스에서 TRIPOD 제거
          canvasEngine.removeEntity(tripodShapeId)
          // Note: 원본 point들이 이미 삭제된 경우 복원 로직이 필요할 수 있음
        }
      }

      if (currentId) {
        canvasEngine.selectEntityById(currentId)
      }
      canvasEngine.restoreEntitiyListening() // 모든 entity listening 복원
      canvasEngine.updateIndex(true)
    } catch (error) {
      console.error('TRIPOD 결합 중 오류:', error)
      App.toastInstance.add('error', 'TRIPOD 결합 중 오류가 발생했습니다.')
    } finally {
      stateStore.setIsLoading(false)
    }
  }

  /**
   * TRIPOD shape을 개별 POINT shape들로 분해하는 함수
   * @param ungroupShapeId - 분해할 TRIPOD shape ID
   */
  async function handleUngroupShape(ungroupShapeId: string): Promise<void> {
    const stateStore = useAppStateStore()
    try {
      stateStore.setIsLoading(true)
      if (!canvasEngine) return

      const tripodEntity = canvasEngine.getEntityById(ungroupShapeId)
      if (!tripodEntity || tripodEntity.entityType !== SHAPES.TRIPOD) {
        App.toastInstance.add('error', 'TRIPOD 도형을 찾을 수 없습니다.')
        return
      }

      // TRIPOD 데이터 추출
      const tripodExportData = canvasEngine.exportEntity(ungroupShapeId)
      if (!tripodExportData || !tripodExportData.tripodShapePoints) {
        App.toastInstance.add('error', 'TRIPOD 데이터를 찾을 수 없습니다.')
        return
      }

      const { dots } = tripodExportData.tripodShapePoints
      if (!dots || dots.length === 0) {
        App.toastInstance.add('error', '분해할 점이 없습니다.')
        return
      }

      // TRIPOD의 balloon 설정들 추출
      const tripodColor = tripodEntity.getColor()

      // 각 dot에 대해 POINT shape 생성
      const newPointEntities: { id: string; shape_data: CanvasEngineExportEntityType }[] = []
      for (let i = 0; i < dots.length; i++) {
        const [x, y] = dots[i]
        // 새로운 POINT entity 생성
        const pointShapeId = canvasEngine.generateShapeId()
        const pointExportData: CanvasEngineExportEntityType = {
          ...tripodExportData,
          type: SHAPES.POINT,
          point: { x, y },
          balloonPoint: { x: 0, y: -70 },
          shapeId: pointShapeId,
          color: tripodColor,
          layerId: tripodExportData.layerId,
        }
        delete pointExportData.tripodShapePoints
        newPointEntities.push({ id: pointShapeId, shape_data: pointExportData })
      }
      if (!App.isApp()) {
        const dataStore = useWebEditorDataStore()
        const tripodShape = dataStore.getShape(ungroupShapeId)
        if (tripodShape) {
          await dataStore.ungroupTripodShapeToHistory(tripodShape, newPointEntities)
        } else {
          throw new Error('TRIPOD shape를 찾을 수 없습니다.')
        }
      } else {
        const shapeStore = useShapeStore()
        const tripodShape = shapeStore.getCurrentShape(ungroupShapeId)
        if (tripodShape) {
          await shapeStore.ungroupTripodShapeToHistory(tripodShape, newPointEntities)
        } else {
          throw new Error('TRIPOD shape를 찾을 수 없습니다.')
        }
      }
      canvasEngine.updateIndex(true)
    } catch (error) {
      console.error('TRIPOD 분해 중 오류:', error)
      App.toastInstance.add('error', 'TRIPOD 분해 중 오류가 발생했습니다.')
    } finally {
      stateStore.setIsLoading(false)
    }
  }

  async function handleTripodTailShape(tripodTailShapeId: string): Promise<void> {
    if (!canvasEngine) return
    const tripodEntity = canvasEngine.getEntityById(tripodTailShapeId)
    if (!tripodEntity || tripodEntity.entityType !== SHAPES.TRIPOD) {
      App.toastInstance.add('error', 'TRIPOD 도형을 찾을 수 없습니다.')
      return
    }
    tripodEntity.handleTripodTailShape(tripodTailShapeId)
  }

  function updateRenderIndex(): void {
    getCanvasRenderer().updateIndex()
  }

  function setRenderLayers(layers: { name: string; id: string }[]): void {
    const renderer = getCanvasRenderer()
    renderer.resetAllLayer()
    renderer.importRenderLayers(layers)
  }

  function setRenderEntity(
    data: CanvasEngineExportEntityType,
    callback: (entity: CanvasEngineEntity) => void,
  ): void {
    const _entity = getCanvasRenderer().importEntity(canvasShapeData(data))
    callback(_entity)
  }

  /**
   * 렌더링용 배경 이미지 설정
   * @param imageUrl 배경 이미지 URL
   * @param rotate 회전 각도 (0, 90, 180, 270)
   */
  async function setRenderBackground(imageUrl: string, rotate: number = 0): Promise<void> {
    await getCanvasRenderer().setRenderBackground(imageUrl, rotate)
  }

  function render(shapes: ShapeForDocs[]): Promise<string | Blob> {
    return getCanvasRenderer().render(shapes)
  }

  function releaseRender(): void {
    getCanvasRenderer().release()
  }

  function release(): void {
    if (!canvasEngine) return
    clearGrabBox() // 그랩박스 정리
    hideAllButtons() // 복사버튼들 정리
    canvasEngine.resetAllLayer()
    canvasEngine = undefined
  }

  function setCanvasEngine(canvas: CanvasEngine): void {
    canvasEngine = canvas
    // 액션버튼그룹 이벤트 리스너 등록
    canvasEngine.setEventListener('onShowButton', showButton)
    canvasEngine.setEventListener('onHideButton', hideButton)
    canvasEngine.setEventListener('onCopyShape', handleCopyShape)
    canvasEngine.setEventListener('onDeleteShapes', handleDeleteShape)
    canvasEngine.setEventListener('onCombineShape', handleCombineShape)
  }

  function getCanvasEngine(): CanvasEngine | undefined {
    return canvasEngine
  }

  function selectShapeById(shapeId: string): void {
    if (!canvasEngine) return
    canvasSelectedShapeId.value = shapeId
    canvasEngine.selectEntityById(shapeId)
  }
  function selectShapeRelated(shapeId: string): void {
    if (!canvasEngine) return
    canvasSelectedShapeId.value = shapeId
    canvasEngine.selectEntityByIdRelated(shapeId)
  }

  function getEntityById(shapeId: string): CanvasEngineEntity | undefined {
    if (!canvasEngine) return
    return canvasEngine.getEntityById(shapeId)
  }

  function getShapeColorById(shapeId: string): CanvasEngineEntity | undefined {
    if (!canvasEngine) return
    return canvasEngine.getEntityById(shapeId)
  }

  function blurShape(): void {
    canvasSelectedShapeId.value = undefined
    hideAllButtons() // 선택 해제 시 모든 복사버튼 숨김
    if (!canvasEngine) return
    canvasEngine.selectedShapeBlur()
  }

  function setShapeId(shapeId: string): void {
    canvasSelectedShapeId.value = shapeId
  }

  function getIndex(shapeId: string): string {
    if (!canvasEngine) return '-1'
    return canvasEngine.getIndex(shapeId)
  }

  function getBalloonText(shapeId: string): string {
    if (!canvasEngine) return ''
    const entity = canvasEngine.getEntityById(shapeId)
    if (!entity) return ''
    return entity.getBalloonText()
  }

  function changeSelectedEntityColor(color: string): void {
    if (!canvasEngine) return
    canvasEngine.changeSelectedEntityColor(color)
  }

  function changeSelectedEntityColorForPreview(shapeId: string, color: string): void {
    if (!canvasEngine) return
    canvasEngine.changeSelectedEntityColorForPreview(shapeId, color)
  }

  function deleteShape(shapeId: string): void {
    if (!canvasEngine) return
    canvasEngine.removeEntity(shapeId)
    canvasEngine.updateIndex()
    bumpIndexVersion()
  }

  function sortEntities(shapeIds: string[]): void {
    if (!canvasEngine) return
    canvasEngine.sortEntities(shapeIds)
    bumpIndexVersion()
  }

  function setReservedIndices(reserved: Set<number>): void {
    if (!canvasEngine) return
    canvasEngine.setReservedIndices(reserved)
  }

  function setPendingCreateBalloonText(text: string | null): void {
    if (!canvasEngine) return
    canvasEngine.setPendingCreateBalloonText(text)
  }

  // 색상필터 등으로 특정 엔티티를 숨김/표시 (클릭·grab 선택 차단)
  function setHiddenEntityIds(ids: Set<string>): void {
    if (!canvasEngine) return
    canvasEngine.setHiddenEntityIds(ids)
  }

  function clearHiddenEntityIds(): void {
    if (!canvasEngine) return
    canvasEngine.clearHiddenEntityIds()
  }

  function refreshCache(): void {
    if (!canvasEngine) return
    canvasEngine.refreshCache()
  }

  function restoreTargetCache(shapeIds: string[]): void {
    if (!canvasEngine) return
    const entities = shapeIds.map(shapeId => canvasEngine?.getEntityById(shapeId))
    if (!entities || entities.length === 0) return
    canvasEngine.restoreTargetCache(entities as CanvasEngineEntity[])
  }

  function showLayer(layerId: string): void {
    if (!canvasEngine) return
    canvasEngine.showLayer(layerId)
  }

  function hideLayer(layerId: string): void {
    if (!canvasEngine) return
    canvasEngine.hideLayer(layerId)
  }

  function createLayer(layerName: string, layerId: string): void {
    if (!canvasEngine) return
    canvasEngine.createLayer(layerName, layerId)
  }

  function selectLayer(layerId: string): void {
    if (!canvasEngine) return
    if (!layerId) {
      const selectedLayer = canvasEngine.getSelectedLayer()
      if (!selectedLayer) return
      canvasEngine.selectLayer(selectedLayer.layerId)
      return
    }
    canvasEngine.selectLayer(layerId)
  }

  function updateIndex(): void {
    if (!canvasEngine) return
    canvasEngine.updateIndex()
    bumpIndexVersion()
  }

  function getEntityLength(): number {
    if (!canvasEngine) return 0
    return canvasEngine.getEntityLength()
  }

  function cancelSelectedShape(): void {
    if (!canvasEngine) return
    blurShape()
    canvasEngine.changeCanvasHandleMode(CANVAS_HANDLE_MODE.DEFAULT)
  }

  async function importEntities<K extends keyof (Defect & Defect)>(
    shapes: Defect[] | Defect[],
    shapeIdKey: K,
  ): Promise<void> {
    for await (const shape of shapes) {
      if (!shape[shapeIdKey as keyof typeof shape]) continue
      if (shape.parent_defect_id) continue // 자식 shape는 건너뜀
      try {
        const entity = canvasEngine?.importEntity({
          ...canvasShapeData(shape.shape_data),
          shapeId: (shape[shapeIdKey as keyof typeof shape] as string).toString(),
          color: shape.color,
          layerId: 'default-layer',
        })
        entity?.changeColor(shape.color)
        applyDefectShapes(shape, entity)
        if (shape.shape_data?.isFill) {
          entity?.setShapeFillToggle(true)
        }
        if (shape.shape_data?.rectType) {
          entity?.setShapeStyleToggle(shape.shape_data.rectType)
        }
        if (shape.shape_data?.dotType) {
          entity?.setIndividualDotTypeSetting(shape.shape_data.dotType)
          entity?.setDotStyleToggle(shape.shape_data.dotType)
        }
        const entityIdStr = (shape[shapeIdKey as keyof typeof shape] as string).toString()
        const hasCurrentPhoto = isWeb
          ? useWebEditorDataStore().getIsPreviousHasDefectPhoto(entityIdStr)
          : useDefectPhotoStore().getIsPreviousHasDefectPhoto(entityIdStr)
        const mode = getDefectDisplayMode(shape, hasCurrentPhoto)

        if (mode === 'previous') {
          // 순수 전차: 보라색 + 동그라미 + 전차넘버링 + 인덱스 숨김
          if (shape.previous_balloon_number) {
            entity?.setBalloonAdminText(shape.previous_balloon_number)
          }
          entity?.setBalloonCircle(true)
          const purpleColor = getCurrentColor('purple')
          entity?.changeColor(purpleColor ?? shape.color)
          entity?.setBalloonFontColor(purpleColor ?? shape.shape_data.balloonFontColor)
          entity?.setBalloonFillColor(getCurrentColor('none') ?? shape.shape_data.balloonFillColor)
          continue
        }

        // 'current' / 'becameCurrent': 일반 인덱스 표시
        entity?.enableIndex()

        // 안전망: shape.color가 보라색이면 잘못 저장된 데이터 → 정상 색상으로 복원
        if (getCurrentColorName(shape.color) === 'purple') {
          const styles = getCurrentCreateStyles()
          const safe = resolveSafeDisplayColors(shape, {
            borderColor: styles.balloon_border_color,
            fontColor: styles.balloon_font_color,
            fillColor: styles.balloon_fill_color,
          })
          entity?.changeColor(safe.borderColor)
          entity?.setBalloonFontColor(safe.fontColor)
          entity?.setBalloonFillColor(safe.fillColor)
        }
      } catch (e) {
        console.error(e)
      }
    }

    updateIndex()
  }

  async function getBase64(hideBrush?: boolean): Promise<string> {
    if (!canvasEngine) {
      throw new Error('CanvasEngine이 초기화되지 않았습니다.')
    }

    // base64로 캔버스를 렌더링 (브러시 숨김·원복은 엔진이 처리)
    return await canvasEngine.toJpegFile('base64', { hideBrush: !!hideBrush })
  }

  // 현재 자동정렬드인지 확인하고 toast 메시지를 호출하는 함수
  function checkAutoAlignModeWithToast(): boolean {
    if (canvasHandleMode.value === CANVAS_HANDLE_MODE.AUTO_ALIGN) {
      App.toastInstance.add(
        'error',
        '자동정렬을 저장하거나 취소를 통해 모드를 종료한 뒤 시도해주세요.',
      )
      return true
    }
    return false
  }

  // 캔버스 새로고침 메소드
  async function refreshCanvas(shapes: Defect[], all: boolean = false): Promise<void> {
    if (!canvasEngine) return

    // 모든 엔티티 제거
    if (all) {
      canvasEngine?.removeAllEntities(true)
    } else {
      const currentEntities = canvasEngine.exportEntities()
      currentEntities.forEach(entity => {
        canvasEngine?.removeEntity(entity.shapeId)
      })
    }

    if (isApp()) {
      await importEntities(shapes as Defect[], 'localStringId')
      canvasEngine.sortEntities(shapes.map(shape => (shape as Defect).localStringId.toString()))
    } else {
      // 새로운 엔티티 가져오기
      await importEntities(shapes, 'id')

      // 엔티티 재정렬
      canvasEngine.sortEntities(shapes.map(shape => shape.id.toString()))
    }

    // 일괄 스타일 변경 후 엔티티 캐시 새로고침
    await canvasEngine.refreshAsyncCache()
  }

  function showBrushEntity(show: boolean): void {
    if (!canvasEngine) return
    canvasEngine.setBrushEntitiesVisible(show)
  }

  // 캔버스 스타일 관련
  function setBalloonScale(scale: number, onlySet: boolean = false): void {
    if (!canvasEngine) return
    balloonScale.value = scale
    canvasEngine.setBalloonScaleGlobal(scale, onlySet)
  }

  function setConnectedLineThicknessScale(scale: number, onlySet: boolean = false): void {
    if (!canvasEngine) return
    connectedLineThicknessScale.value = scale
    canvasEngine.setConnectedLineThicknessScaleGlobal(scale, onlySet)
  }

  function setBalloonFontScale(scale: number, onlySet: boolean = false): void {
    if (!canvasEngine) return
    balloonFontScale.value = scale
    canvasEngine.setBalloonFontScaleGlobal(scale, onlySet)
  }

  function setPointShapeScale(scale: number, onlySet: boolean = false): void {
    if (!canvasEngine) return
    pointShapeScale.value = scale
    canvasEngine.setPointShapeScaleGlobal(scale, onlySet)
  }

  function setAreaBorderThicknessScale(scale: number, onlySet: boolean = false): void {
    if (!canvasEngine) return
    areaBorderThicknessScale.value = scale
    canvasEngine.setAreaBorderThicknessScaleGlobal(scale, onlySet)
  }

  function setBalloonFontColor(color: string): void {
    if (!canvasEngine) return
    balloonFontColor.value = color
    canvasEngine.setBalloonFontColorGlobal(color)
  }
  function setBalloonFillColor(color: string): void {
    if (!canvasEngine) return
    balloonFillColor.value = color
    canvasEngine.setBalloonFillColorGlobal(color)
  }
  function setBalloonBorderColor(color: string): void {
    if (!canvasEngine) return
    balloonBorderColor.value = color
    canvasEngine.setBalloonBorderColorGlobal(color)
  }
  function setBalloonFillOpacity(opacity: number): void {
    if (!canvasEngine) return
    balloonFillOpacity.value = opacity
    canvasEngine.setBalloonFillOpacityGlobal(opacity)
  }

  function setViewport(floorplanId: string): boolean {
    if (!canvasEngine) return false
    const viewport = getSavedViewport(floorplanId)
    if (!viewport) return false
    const { x, y, zoom } = viewport
    canvasEngine.setViewport(x, y, zoom)
    return true
  }

  function saveViewport(floorplanId: string, x: number, y: number, zoom: number): void {
    if (!canvasEngine) return
    localStorage.setItem(floorplanId, JSON.stringify({ x, y, zoom }))
  }

  function getSavedViewport(
    floorplanId: string,
  ): { x: number; y: number; zoom: number } | undefined {
    if (!canvasEngine) return
    const viewport = localStorage.getItem(floorplanId)
    if (!viewport) return
    const { x, y, zoom } = JSON.parse(viewport)
    return { x, y, zoom }
  }

  function setAllFloorNumbering(isNumbering: boolean): void {
    if (!canvasEngine) return
    isAllFloorNumbering.value = isNumbering
    canvasEngine.setAllFloorNumbering(isNumbering)
    canvasEngine.updateIndex()
  }

  function setAllFloorIndex(index: number): void {
    if (!canvasEngine) return
    canvasEngine.setAllFloorIndex(index)
  }

  /** animate=false면 애니메이션 없이 즉시 맞추고 저장 (층 진입 시 배경 첫 그리기 전 뷰포트용) */
  function fitView(floorplanId?: string, animate = true): void {
    if (!canvasEngine) return

    try {
      // 스테이지와 배경 크기 가져오기
      const { width: stageWidth, height: stageHeight } = canvasEngine.getViewportSize()
      const backgroundWidth = canvasEngine.getBackgroundWidth()
      const backgroundHeight = canvasEngine.getBackgroundHeight()

      // 배경이 설정되지 않은 경우 처리
      if (!backgroundWidth || !backgroundHeight) {
        console.warn('배경 도면이 설정되지 않았습니다.')
        return
      }

      // 배경 도면의 중심점 계산
      const backgroundCenterX = backgroundWidth / 2
      const backgroundCenterY = backgroundHeight / 2

      // 화면에 맞는 스케일 계산 (여백 5% 추가)
      const margin = 0.05
      const scaleX = (stageWidth * (1 - margin)) / backgroundWidth
      const scaleY = (stageHeight * (1 - margin)) / backgroundHeight
      const scale = Math.min(scaleX, scaleY, canvasEngine.getMaxZoom())

      // 최소 줌 제한 적용
      const finalScale = Math.max(scale, canvasEngine.getMinZoom())

      // 배경 도면이 화면 중앙에 오도록 위치 계산
      const targetX = stageWidth / 2 - backgroundCenterX * finalScale
      const targetY = stageHeight / 2 - backgroundCenterY * finalScale

      if (!animate) {
        canvasEngine.setViewport(targetX, targetY, finalScale)
        if (floorplanId) saveViewport(floorplanId, targetX, targetY, finalScale)
        return
      }

      // 부드러운 애니메이션 (엔진 API)
      canvasEngine.animateViewport(targetX, targetY, finalScale, {
        duration: 0.2,
        easing: 'easeInOut',
        onFinish: (): void => {
          if (floorplanId) {
            saveViewport(floorplanId, targetX, targetY, finalScale)
          }
        },
      })
    } catch (error) {
      console.error('FitView 실행 중 오류 발생:', error)
    }
  }

  //canvas 컨트롤 관련

  //특정 shape 삭제
  function canvasDeleteShape(shapeId: string): void {
    if (!canvasEngine) return
    canvasEngine.removeEntity(shapeId)
    canvasEngine.updateIndex()
  }

  //특정 shape 추가
  //currentIndex가 있으면 해당 위치에 추가
  function canvasAddShape(shape: Defect | Defect, currentIndex?: number): void {
    if (!canvasEngine) return
    if (shape.parent_defect_id) return
    if (!shape.shape_data) return
    const currentShapeId = isApp() ? (shape as Defect).localStringId : shape.id.toString()
    if (!currentShapeId) return
    canvasEngine.importEntityToCurrentIndex(
      {
        ...canvasShapeData(shape.shape_data),
        shapeId: currentShapeId,
        color: shape.color,
        layerId: 'default-layer',
      },
      currentIndex,
    )

    const entity = getEntityById(currentShapeId)
    if (!entity) return
    entity?.changeColor(shape.color)
    applyDefectShapes(shape, entity)
    if (shape.shape_data?.isFill) {
      entity?.setShapeFillToggle(true)
    }
    if (shape.shape_data?.rectType) {
      entity?.setShapeStyleToggle(shape.shape_data.rectType)
    }
    if (shape.shape_data?.dotType) {
      entity?.setIndividualDotTypeSetting(shape.shape_data.dotType)
      entity?.setDotStyleToggle(shape.shape_data.dotType)
    }

    const hasCurrentPhoto = isApp()
      ? useDefectPhotoStore().getIsPreviousHasDefectPhoto(currentShapeId)
      : useWebEditorDataStore().getIsPreviousHasDefectPhoto(currentShapeId)
    const mode = getDefectDisplayMode(shape, hasCurrentPhoto)

    if (mode === 'previous') {
      // 순수 전차: 보라색 + 동그라미 + 전차넘버링
      if (shape.previous_balloon_number) {
        entity?.setBalloonAdminText(shape.previous_balloon_number)
      }
      entity?.setBalloonCircle(true)
      entity?.changeColor(shapeColorItems.find(item => item.name == 'purple')?.color ?? shape.color)
    } else {
      // 'current' / 'becameCurrent': 일반 인덱스 표시
      entity?.enableIndex()
      // 안전망: shape.color가 보라색이면 잘못 저장된 데이터 → 정상 색상으로 복원
      if (getCurrentColorName(shape.color) === 'purple') {
        const styles = getCurrentCreateStyles()
        const safe = resolveSafeDisplayColors(shape, {
          borderColor: styles.balloon_border_color,
          fontColor: styles.balloon_font_color,
          fillColor: styles.balloon_fill_color,
        })
        entity?.changeColor(safe.borderColor)
        entity?.setBalloonFontColor(safe.fontColor)
        entity?.setBalloonFillColor(safe.fillColor)
      }
    }

    UTIL.restoreTargetCache([entity])
  }

  function reloadShape(shape: Defect | Defect): void {
    if (!canvasEngine) return
    if (!shape.shape_data) return
    const currentShapeId = isApp() ? (shape as Defect).localStringId : shape.id.toString()
    const currentIndex = canvasEngine.getEntities().findIndex(e => e.getId() == currentShapeId)
    // NO_SHAPE 였던 결함에 도형이 연결된 경우(redo 등)는 캔버스에 엔티티가 없다 — removeEntity 는 throw 하므로 가드
    if (currentIndex >= 0) canvasEngine.removeEntity(currentShapeId)
    canvasAddShape(shape, currentIndex >= 0 ? currentIndex : undefined)
  }

  // 캔버스에 엔티티가 있으면 제거 (도형 연결 취소(undo) 등 NO_SHAPE 복귀 시)
  function removeEntityIfExists(shapeId: string): void {
    if (!canvasEngine) return
    if (!canvasEngine.getEntityById(shapeId)) return
    canvasEngine.removeEntity(shapeId)
  }

  function generateShapeId(): string {
    if (!canvasEngine) return ''
    return canvasEngine.generateShapeId()
  }

  function updateShapeLayerId(shapeId: string, layerId: string): void {
    if (!canvasEngine) return
    canvasEngine.updateEntityLayerId(shapeId, layerId)
  }

  function clearSystemLayer(): void {
    if (!canvasEngine) return
    canvasEngine.clearSystemLayer()
  }

  const userConfigSetting = ref<UserSettingConfigConv>({
    userSetBalloonScale: 1.0,
    userSetConnectedLineThicknessScale: 1.0,
    userSetBalloonFontScale: 1.0,
    userSetPointShapeScale: 1.0,
    userSetAreaBorderThicknessScale: 1.0,
    userSetBalloonStyleBordeColor: 'red',
    userSetBalloonStyleFillColor: 'none',
    userSetBalloonStyleFontColor: 'red',
    userSetBalloonFillOpacity: 1.0,
  })
  function getUserSettingLocal(): UserSettingConfig[] {
    const localData = localStorage.getItem('userSettingConfig')
    if (localData) {
      const parsedData = JSON.parse(localData)
      if (parsedData) {
        userConfigSetting.value = convertUserSettingToCanvasStore(parsedData)
        return parsedData
      }
      return []
    }
    return []
  }

  function applyDefectShapes(shape: Defect, entity: CanvasEngineEntity | undefined): void {
    if (!entity) return
    entity?.setBalloonFontColor(
      shape.shape_data.balloonFontColor ? shape.shape_data.balloonFontColor : shape.color,
    )
    entity?.setBalloonFillColor(
      shape.shape_data.balloonFillColor ? shape.shape_data.balloonFillColor : '#fff',
    )
    entity?.setBalloonScale(shape.shape_data.balloonScale)
    entity?.setConnectedLineThicknessScale(shape.shape_data.connectedLineThicknessScale)
    entity?.setBalloonFontScale(shape.shape_data.balloonFontScale)
    entity?.setPointShapeScale(shape.shape_data.pointShapeScale)
    entity?.setAreaBorderThicknessScale(shape.shape_data.areaBorderThicknessScale)
    if (entity && entity.isTripod() && shape.shape_data.tripodShapePoints?.tailStyle) {
      entity.setTailStyle(shape.shape_data.tripodShapePoints.tailStyle)
    }
  }

  // grabBox 관련 메서드들
  function setGrabbedShapeIds(entityIds: string[]): void {
    grabbedEntityIds.value = entityIds
    // grab된 도형이 있으면 grabMode 활성화, 없으면 비활성화
    isGrabMode.value = entityIds.length > 0
  }
  function clearGrab(): void {
    grabbedEntityIds.value = []
    grabBoxRect.value = null
    isGrabMode.value = false
  }

  function setGrabBoxRect(
    rect: { x: number; y: number; width: number; height: number } | null,
  ): void {
    grabBoxRect.value = rect
  }

  function clearGrabBox(): void {
    grabbedEntityIds.value = []
    grabBoxRect.value = null
    isGrabMode.value = false
    canvasEngine?.exitGrabMode()
  }

  function enableMobileSelectMode(): void {
    isMobileSelectMode.value = true
    canvasEngine?.enableMobileSelectMode()
  }

  function disableMobileSelectMode(): void {
    isMobileSelectMode.value = false
    canvasEngine?.disableMobileSelectMode()
  }

  async function setDefaultLayers(): Promise<void> {
    if (!canvasEngine) return

    await canvasEngine.resetAllLayer()
    selectLayer('')

    const backgroundLayer = { name: '배경', id: 'background-layer' }
    const defaultLayer = { name: '기본', id: 'default-layer' }
    canvasEngine.createLayer(backgroundLayer.name, backgroundLayer.id.toString())
    canvasEngine.createLayer(defaultLayer.name, defaultLayer.id.toString())

    canvasEngine.selectLayer(defaultLayer.id.toString())
  }

  function setCreateStyles(type: string, value: number | string): void {
    if (!canvasEngine) return
    switch (type) {
      case 'balloon_scale':
        canvasEngine.forCreateBalloonScale = Number(value)
        break
      case 'connected_line_thickness_scale':
        canvasEngine.forCreateConnectedLineThicknessScale = Number(value)
        break
      case 'balloon_font_scale':
        canvasEngine.forCreateBalloonFontScale = Number(value)
        break
      case 'point_shape_scale':
        canvasEngine.forCreatePointShapeScale = Number(value)
        break
      case 'area_shape_border_thickness_scale':
        canvasEngine.forCreateAreaBorderThicknessScale = Number(value)
        break
      case 'balloon_font_color':
        canvasEngine.forCreateBalloonFontColor = String(value)
        break
      case 'balloon_fill_color':
        canvasEngine.forCreateBalloonFillColor = String(value)
        break
      case 'balloon_border_color':
        canvasEngine.forCreateBalloonBorderColor = String(value)
        break
      case 'balloonFillOpacity':
        canvasEngine.forCreateBalloonFillOpacity = Number(value)
        break
    }
  }

  function setGlobalFloorStyles(type: string, value: number | string): void {
    if (!canvasEngine) return
    switch (type) {
      case 'balloon_scale':
        canvasEngine.globalBalloonScale = Number(value)
        break
      case 'connected_line_thickness_scale':
        canvasEngine.globalConnectedLineThicknessScale = Number(value)
        break
      case 'balloon_font_scale':
        canvasEngine.globalBalloonFontScale = Number(value)
        break
      case 'point_shape_scale':
        canvasEngine.globalPointShapeScale = Number(value)
        break
      case 'area_shape_border_thickness_scale':
        canvasEngine.globalAreaBorderThicknessScale = Number(value)
        break
      case 'balloon_font_color':
        canvasEngine.globalBalloonFontColor = String(value)
        break
      case 'balloon_fill_color':
        canvasEngine.globalBalloonFillColor = String(value)
        break
      case 'balloon_border_color':
        canvasEngine.globalBalloonBorderColor = String(value)
        break
      case 'balloonFillOpacity':
        canvasEngine.globalBalloonFillOpacity = Number(value)
        break
    }
  }

  function getCurrentCreateStyles(): {
    balloon_font_color: string
    balloon_fill_color: string
    balloon_border_color: string
    balloon_fill_opacity: number
  } {
    if (!canvasEngine)
      return {
        balloon_font_color: '',
        balloon_fill_color: '',
        balloon_border_color: '',
        balloon_fill_opacity: 1,
      }
    return {
      balloon_font_color: canvasEngine.forCreateBalloonFontColor,
      balloon_fill_color: canvasEngine.forCreateBalloonFillColor,
      balloon_border_color: canvasEngine.forCreateBalloonBorderColor,
      balloon_fill_opacity: canvasEngine.forCreateBalloonFillOpacity,
    }
  }

  function destroyCanvasEngine(): void {
    if (!canvasEngine) return
    canvasEngine.destroy()
  }

  function canvasNotFoundHandler(shapeId: string, env: 'app' | 'web'): CanvasNotFoundHandler {
    if (!canvasEngine) {
      return { env, canvasState: 'NONE', shapeId }
    }
    const entity = canvasEngine.getEntityById(shapeId)
    if (!entity) {
      return { env, canvasState: 'NONE', shapeId }
    }
    const shapeNum = getIndex(shapeId)
    return {
      env,
      canvasState: 'SETTED',
      shapeId,
      shapeNum: !Number(shapeNum) || Number(shapeNum) == -1 ? undefined : Number(shapeNum),
    }
  }

  // 캔버스에서 도형 생성 직후 플래그 (localStorage.isCreate 대체)
  const isNewlyCreatedShape = ref<boolean>(false)
  function setIsNewlyCreatedShape(value: boolean): void {
    isNewlyCreatedShape.value = value
  }

  return {
    setAllFloorNumbering,
    isAllFloorNumbering,
    isSnappingEnabled,
    setCanvasEngine,
    getCanvasEngine,
    selectShapeById,
    selectShapeRelated,
    setShapeId,
    canvasSelectedShapeId,
    fitView,
    setDefaultLayers,
    getIndex,
    getBalloonText,
    changeSelectedEntityColor,
    deleteShape,
    showLayer,
    createLayer,
    selectLayer,
    blurShape,
    getEntityById,
    updateIndex,
    getEntityLength,
    hideLayer,
    sortEntities,
    setReservedIndices,
    setPendingCreateBalloonText,
    setHiddenEntityIds,
    clearHiddenEntityIds,
    refreshCache,
    restoreTargetCache,
    importEntities,
    getCanvasPointSize,
    getBase64,
    setRenderLayers,
    setRenderEntity,
    setRenderBackground,
    render,
    releaseRender,
    updateRenderIndex,
    release,
    canvasHandleMode,
    checkAutoAlignModeWithToast,
    canvasNotFoundHandler,
    refreshCanvas,
    showBrushEntity,
    cancelSelectedShape,
    userConfigSetting,
    balloonScale,
    connectedLineThicknessScale,
    balloonFontScale,
    pointShapeScale,
    areaBorderThicknessScale,
    balloonFontColor,
    balloonFillColor,
    balloonBorderColor,
    balloonFillOpacity,
    setBalloonScale,
    setConnectedLineThicknessScale,
    setBalloonFontScale,
    setPointShapeScale,
    setAreaBorderThicknessScale,
    setBalloonFontColor,
    setBalloonFillColor,
    setBalloonBorderColor,
    setBalloonFillOpacity,
    getShapeColorById,
    changeSelectedEntityColorForPreview,
    setViewport,
    saveViewport,
    setAllFloorIndex,
    getCanvasRenderer,
    // 복사버튼 관련 함수들
    showButton,
    hideButton,
    hideAllButtons,
    handleCopyShape,
    initUserSettings,
    handleDeleteShape,
    handleUngroupShape,
    handleAttachShape,
    handleTripodTailShape,

    //canvas 컨트롤 관련
    canvasDeleteShape,
    canvasAddShape,
    reloadShape,
    removeEntityIfExists,
    generateShapeId,
    updateShapeLayerId,
    clearSystemLayer,
    getUserSettingLocal,

    // grabBox 관련 메서드들
    setGrabbedShapeIds,
    clearGrab,
    setGrabBoxRect,
    clearGrabBox,
    grabbedEntityIds,
    grabBoxRect,
    isGrabMode,
    isMobileSelectMode,
    enableMobileSelectMode,
    disableMobileSelectMode,

    // 스타일 관련
    setCreateStyles,
    setGlobalFloorStyles,
    getCurrentCreateStyles,
    destroyCanvasEngine,
    indexVersion,
    bumpIndexVersion,

    // 도형 신규 생성 플래그 (localStorage.isCreate 대체)
    isNewlyCreatedShape,
    setIsNewlyCreatedShape,

    // 외부에서 pending link 모드 요청
    pendingLinkRequest,
    requestPendingLink,
    clearPendingLinkRequest,
  }
})
